import React, { useEffect, useState } from "react";
import { RichTreeView } from "@mui/x-tree-view";
import SearchIcon from "@mui/icons-material/Search";
import { Box, IconButton, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import ApiService from "../../../utils/fetch";
import {
  setManageForecastTreeData,
  setManageForecastBottlerData,
  setManageForecastFinalTreeData,
} from "../../../redux/entitySlice";
//import { setBottlerData, setFinalTreeData, setSelectedTreeIds, setTransformedTreeData } from "../../redux/entitySlice";
import { useBottlerSelection } from "../../../context/BottlerSelectionContext";

const BottlerSelectionAccordion = ({
  selectedPeriod,
  selectedTerm,
  hasSameSubmission,
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [filteredTreeData, SetFilteredTreeData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const dispatch = useDispatch();
  const { setSelectedData } = useBottlerSelection();
  const manageForecastTreeDataRedux = useSelector(
    (state) => state.entity.manageForecastTreeData,
  );

  //Event handler for when selected items change
  const handleSelectedItemsChange = (_event, ids) => {
    const newSelectedIds = determineIdsToSet(filteredTreeData, ids, selectedIds);

    setSelectedIds(newSelectedIds);

    const filteredItems = newSelectedIds.filter((item) =>
      item.startsWith("plant"),
    );
    const ApplyTreeViewData = filterBottlersByPlant(filteredItems, treeData);

    const bottler = getBottlerForFirstCustomer(newSelectedIds, treeData);
    dispatch(setManageForecastBottlerData(bottler));
    setSelectedData(ApplyTreeViewData);
  };

  const getBottlerForFirstCustomer = (selectedIds, treeData) => {
    // 1. Prefer explicit customer selection
    const customerId = selectedIds.find((id) => id.startsWith("customer"));

    // 2. Otherwise infer from a plant
    const plantId =
      customerId == null
        ? selectedIds.find((id) => id.startsWith("plant"))
        : null;

    if (!customerId && !plantId) return null;

    for (const bottler of treeData) {
      for (const customer of bottler.children || []) {
        // Customer explicitly selected
        if (customer.id === customerId) {
          return bottler.label;
        }

        // Plant selected → infer customer
        if (plantId) {
          for (const plant of customer.children || []) {
            if (plant.id === plantId) {
              return bottler.label;
            }
          }
        }
      }
    }

    return null;
  };

  const transformData = (data) => {
    return data.map((bottler, bottlerIndex) => ({
      id: `bottler${bottlerIndex + 1}`,
      label: bottler.bottler,
      children: bottler.customers.map((customer, customerIndex) => ({
        id: `customer${bottlerIndex + 1}-${customerIndex + 1}`,
        label: `${customer.customerID}`,
        customerID: customer.customerID,
        children: customer.locationInfoList.map(
          (locationInfo, locationIndex) => ({
            id: `plant${bottlerIndex + 1}-${customerIndex + 1}-${locationIndex + 1}`,
            label: ` ${locationInfo.location}`,
            locationId: locationInfo.location,
            shortTermPeriodList: locationInfo.applicableShortTermPeriodList,
            longTermPeriodList: locationInfo.applicableLongTermPeriodList,
          }),
        ),
      })),
    }));
  };

  useEffect(() => {
    if (
      manageForecastTreeDataRedux === null ||
      manageForecastTreeDataRedux.length === 0
    ) {
      const getData = async () => {
        try {
          const response = await ApiService.get(
            "v1/manage-forecast/bottler-selection",
          );

          // const bottler = response.data[0]?.bottler;
          // dispatch(setManageForecastBottlerData(bottler));

          const transformedData = transformData(response.data);
          setTreeData(transformedData);
          SetFilteredTreeData(transformedData);

          const allIds = getAllIds(transformedData);
          dispatch(setManageForecastTreeData(transformedData));
          const filteredItems = allIds.filter((item) =>
            item.startsWith("plant"),
          );
          const ApplyTreeViewData = filterBottlersByPlant(
            filteredItems,
            transformedData,
          );
          dispatch(setManageForecastFinalTreeData(ApplyTreeViewData));
        } catch (error) {
          console.error("Error posting data", error);
        }
      };
      getData();
    } else {
      setTreeData(manageForecastTreeDataRedux);
      SetFilteredTreeData(manageForecastTreeDataRedux);
    }
  }, []);

  useEffect(() => {
    filterTreeDataBasedOnPeriodTerm(selectedPeriod, selectedTerm);
  }, [selectedPeriod, selectedTerm]);

  const filterTreeDataBasedOnPeriodTerm = (selectedPeriod, selectedTerm) => {
    // for same submission (skip filtering)
    if (hasSameSubmission) return;

    const isValid = selectedPeriod && selectedTerm;
    const periodKey = selectedPeriod?.split("-").slice(0, 3).join("-"); // e.g., 'FM-09-2025'

    // Reset selection always before filter
    setSelectedIds([]);
    setSelectedData([]);

    // If invalid selection, reset to full tree
    if (!isValid) {
      SetFilteredTreeData(manageForecastTreeDataRedux);
      setTreeData(manageForecastTreeDataRedux);
      return;
    }

    // Determine which list to filter on
    const isShortTerm = selectedTerm === "Short Term";
    const listKey = isShortTerm ? "shortTermPeriodList" : "longTermPeriodList";

    // Filter the tree
    const filteredTree = manageForecastTreeDataRedux
      .map((bottler) => {
        const filteredCustomers = bottler.children
          .map((customer) => {
            const filteredPlants = customer.children.filter((plant) =>
              plant[listKey]?.some((p) => p.startsWith(periodKey)),
            );

            return filteredPlants.length > 0
              ? { ...customer, children: filteredPlants }
              : null;
          })
          .filter(Boolean);

        return filteredCustomers.length > 0
          ? { ...bottler, children: filteredCustomers }
          : null;
      })
      .filter(Boolean);

    SetFilteredTreeData(filteredTree);
    setTreeData(filteredTree);
  };

  const getAllIds = (items) => {
    const ids = [];
    items.forEach((item) => {
      ids.push(item.id);
      if (item.children) {
        ids.push(...getAllIds(item.children));
      }
    });
    return ids;
  };

  const filterBottlersByPlant = (plantIds, products) => {
    return products
      .map((bottler) => {
        const filteredCustomers = bottler.children
          .map((customer) => {
            const matchingPlants = customer.children.filter((plant) =>
              plantIds.includes(plant.id),
            );
            return matchingPlants.length > 0
              ? {
                  customerId: customer.customerID.split("-")[0], // Extract the part before '-'
                  locationId: matchingPlants.map(
                    (plant) => plant.locationId.split("-")[0],
                  ), // Extract the part before '-'
                }
              : null;
          })
          .filter(Boolean);

        return filteredCustomers.length > 0
          ? { bottler: bottler.label, customerList: filteredCustomers }
          : null;
      })
      .filter(Boolean)
      .flatMap((bottler) =>
        bottler.customerList.flatMap((customer) =>
          customer.locationId.map((locationId) => ({
            locationId,
            customerId: customer.customerId,
          })),
        ),
      );
  };

  //Function to determine which IDs should be set based on the current and new selections
  function determineIdsToSet(items, newIds, currentIds) {
    const isDeselectingNode = currentIds.length > newIds.length;
    if (isDeselectingNode) {
      const removed = currentIds.filter((id) => !newIds.includes(id))[0];
      const parentIdsToRemove = getAllParentIds(items, removed);
      const childIdsToRemove = getSelectedIdsAndChildrenIds(items, [removed]);
      const newIdsWithParentsAndChildrenRemoved = newIds.filter(
        (id) =>
          !parentIdsToRemove.includes(id) && !childIdsToRemove.includes(id),
      );
      return newIdsWithParentsAndChildrenRemoved;
    }
    const added = newIds.filter((id) => !currentIds.includes(id))[0];
    const idsToSet = getSelectedIdsAndChildrenIds(items, newIds);
    let parent = getParentNode(items, added);
    while (parent) {
      const childIds = parent.children?.map((node) => node.id) ?? [];
      const allChildrenSelected = childIds.every((id) => idsToSet.includes(id));
      if (allChildrenSelected) {
        idsToSet.push(parent.id);
        parent = getParentNode(items, parent.id);
      } else {
        break;
      }
    }
    return idsToSet;
  }

  // Helper function to get selected IDs along with their children
  function getSelectedIdsAndChildrenIds(items, selectedIds) {
    const selectedIdIncludingChildrenIds = new Set([...selectedIds]);
    for (const item of items) {
      if (selectedIds.includes(item.id)) {
        selectedIdIncludingChildrenIds.add(item.id);
        if (item.children) {
          const childrenIds = item.children.map((child) => child.id);
          const childrenSelectedIds = getSelectedIdsAndChildrenIds(
            item.children,
            childrenIds,
          );
          childrenSelectedIds.forEach((selectedId) =>
            selectedIdIncludingChildrenIds.add(selectedId),
          );
        }
      } else if (item.children) {
        const childrenSelectedIds = getSelectedIdsAndChildrenIds(
          item.children,
          selectedIds,
        );
        childrenSelectedIds.forEach((selectedId) =>
          selectedIdIncludingChildrenIds.add(selectedId),
        );
      }
    }
    return [...Array.from(selectedIdIncludingChildrenIds)];
  }

  // Helper function to find the parent node of a given item ID
  function getParentNode(items, id) {
    for (const item of items) {
      if (item.children) {
        if (item.children.some((child) => child.id === id)) {
          return item;
        } else {
          const parentNode = getParentNode(item.children, id);
          if (parentNode) {
            return parentNode;
          }
        }
      }
    }
    return undefined;
  }

  // Helper function to get all parent IDs of a given item ID
  function getAllParentIds(items, id) {
    const parentIds = [];
    let parent = getParentNode(items, id);
    while (parent) {
      parentIds.push(parent.id);
      parent = getParentNode(items, parent.id);
    }
    return parentIds;
  }

  //handle Search change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    SetFilteredTreeData(filterTreeData(treeData, event.target.value));
  };

  //Filter Tree Data
  const filterTreeData = (items, query) => {
    if (!query) {
      return items;
    }
    return items
      .filter((item) => {
        const matches = item.label.toLowerCase().includes(query.toLowerCase());
        const children = item.children
          ? filterTreeData(item.children, query)
          : [];
        return matches || children.length > 0;
      })
      .map((item) => {
        const matches = item.label.toLowerCase().includes(query.toLowerCase());
        // If parent matches query, include ALL children (unfiltered)
        // If parent doesn't match, only include children that match
        const children = matches
          ? item.children
          : item.children ? filterTreeData(item.children, query) : [];
        return {
          ...item,
          children: children,
        };
      });
  };

  return (
    <React.Fragment>
                  <Box sx={{
                height: "80vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Search"
        margin="normal"
        value={searchQuery}
        onChange={handleSearchChange}
        sx={{
          "& .MuiInputBase-input": {
            padding: "6px 8px",
            fontSize: "0.85rem",
          },
        }}
        InputProps={{
          endAdornment: (
            <IconButton size="small">
              <SearchIcon fontSize="small" />
            </IconButton>
          ),
        }}
      />
      <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2, pt: 0 }}>
      <RichTreeView
        //defaultExpandedItems={["all_plants"]}
        multiSelect
        checkboxSelection
        selectedItems={selectedIds}
        onSelectedItemsChange={handleSelectedItemsChange}
        items={filteredTreeData}
        sx={{
          flexGrow: 1,
          backgroundColor: "white",
          "& .MuiTreeItem-label": {
            fontSize: "11px",
          },
        }}
      />
      </Box>
      </Box>
    </React.Fragment>
  );
};

export default BottlerSelectionAccordion;
